import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../lib/api';
import { useLanguage } from './LanguageContext';

const PageContext = createContext(null);

/**
 * usePage(key) - fetches a page's full content document and exposes a t() helper
 * scoped to the current language.
 */
export function PageProvider({ children }) {
  const [pages, setPages] = useState({});
  const [loadingKeys, setLoadingKeys] = useState({});

  const fetchPage = async (key) => {
    if (pages[key] || loadingKeys[key]) return pages[key];
    setLoadingKeys((s) => ({ ...s, [key]: true }));
    try {
      const r = await api.get(`/public/pages/${key}`);
      setPages((s) => ({ ...s, [key]: r.data }));
      return r.data;
    } catch (e) {
      return null;
    } finally {
      setLoadingKeys((s) => ({ ...s, [key]: false }));
    }
  };

  return (
    <PageContext.Provider value={{ pages, fetchPage }}>
      {children}
    </PageContext.Provider>
  );
}

export function usePage(key) {
  const ctx = useContext(PageContext);
  const { lang } = useLanguage();
  const [data, setData] = useState(ctx?.pages?.[key] || null);

  useEffect(() => {
    if (!ctx) return;
    if (ctx.pages[key]) {
      setData(ctx.pages[key]);
    } else {
      ctx.fetchPage(key).then((d) => setData(d));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ctx?.pages?.[key]]);

  const t = (slot, fallback = '') => {
    if (!data) return fallback;
    const c = data.content || {};
    return (c[lang] && c[lang][slot]) || (c.en && c.en[slot]) || fallback;
  };

  const seo = (() => {
    if (!data) return {};
    const s = data.seo || {};
    return s[lang] || s.en || {};
  })();

  return { data, t, seo, lang };
}
